/* eslint-disable no-useless-escape */
/* eslint-disable react/no-unstable-nested-components */
import React, { InputHTMLAttributes, useEffect, useState } from 'react';
import { EmailInput, FullScreenLoading, LargeButton, PassInput, RouterLink } from 'pg-ui-library';
import { useTranslation } from 'react-i18next';
import logoSvg from 'src/assets/logo_pride.svg';
import background from 'src/assets/signUpBg.png';
import { ErrorMessage, Form, Formik } from 'formik';
import * as yup from 'yup';
import {
  ISecurityQuestion,
  ISecurityQuestionAnswer,
  ISignup,
  ISignupLookup,
  ISignupWithQuestions,
} from 'src/interfaces';
import { useNavigate, useSearchParams } from 'react-router-dom';

import getSecurityQuestionsService from 'src/services/get-security-questions.service';
import SignupService from 'src/services/signup.service';
import { SecurityQuestionsModal } from 'src/components/security-questions-modal';
import personSVG from 'src/assets/personDark.svg';
import {
  Step1Wrapper,
  BackgroundImage,
  Container,
  FormContainer,
  FooterContainer,
  FooterText,
  MenuContainer,
  FormTitle,
  ImageContainer,
  LinkContainer,
  Logo,
  Text,
  GrayText,
  RedText,
  ErrorText,
  UsernameInput,
  InputIcon,
  SignUpInput,
  Input,
} from './styles';

type CustomDateWithPlaceholderProps = InputHTMLAttributes<HTMLInputElement>;

const CustomDateWithPlaceholder: React.FC<CustomDateWithPlaceholderProps> = ({ ...rest }) => {
  const [inputType, setInputType] = useState<string>('text');

  const handleFocus = () => {
    setInputType('date');
  };
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <Input {...rest} type={inputType} onFocus={handleFocus} />;
};

const SignUp: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const validationSchema = yup.object({
    email: yup.string().email(t('global.form.email')),
    username: yup
      .string()
      .required(t('global.form.required'))
      .matches(/^(?=.*[a-zA-Z])[A-Za-z0-9\-_@.]{4,}$/, t('global.form.username')),
    password: yup
      .string()
      .required(t('global.form.required'))
      .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/),
    confirmPassword: yup.string().oneOf([yup.ref('password'), null], t('signUp.form.passwordRestrictions.item06')),
    mfaPreference: yup.string().required('Please select a verification method').oneOf(['email', 'authenticator']),
  });
  const [signupError, setSignupError] = useState(false);
  const [signupStep1Error, setSignupStep1Error] = useState(false);
  const [signupStep1ErrorMessage, setSignupStep1ErrorMessage] = useState(
    'An unknown error occurred during signup. Please try again.'
  );
  const [openSecurityQuestionsModal, setOpenSecurityQuestionsModal] = useState(false);
  const [securityQuestionAnswer, setSecurityQuestionAnswer] = useState<ISecurityQuestionAnswer[]>([]);
  const [signupValidValues, setSignupValidValues] = useState<ISignup>();

  const [step1Data, setStep1Data] = useState<ISignupLookup>({
    businessEmail: '',
    lastNumbersSsn: '',
    birthDate: '',
  });
  const loadSecurityQuestions = async () => {
    const data = await getSecurityQuestionsService.getQuestions();

    const questions = data.map((question: ISecurityQuestion) => ({
      questionId: question.id,
      answer: '',
      question: question.question,
    }));

    setSecurityQuestionAnswer(questions);
  };

  useEffect(() => {
    loadSecurityQuestions();
  }, []);

  const handleSecurityQuestions = async (values: ISignup) => {
    setSignupValidValues(values);
    setOpenSecurityQuestionsModal(true);
  };

  const submit = async (values: ISignupWithQuestions) => {
    try {
      setLoading(true);
      const signUpToken = searchParams.get('token') || '';
      await SignupService.signup(values, signUpToken);
      setSignupError(false);
      navigate('/mfa', {
        state: {
          username: values.username,
          password: values.password,
          preference: values.mfaPreference,
          mfaActivated: false,
        },
      });
    } catch (err: any) {
      setSignupError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleData = (info: { [key: string]: string }) => {
    setStep1Data({ ...step1Data, ...info });
  };

  const handleVerification = async () => {
    if (step1Data.birthDate.trim() === '' || !step1Data.birthDate) {
      setSignupStep1Error(true);
      setSignupStep1ErrorMessage('Please fill the birthdate');
      return;
    }
    setSignupStep1Error(false);
    setSignupStep1ErrorMessage('');

    try {
      setLoading(true);
      const response = await SignupService.employeeSignupLookup(step1Data);
      navigate(`/sign-up?token=${response}`);
    } catch (e) {
      setSignupStep1Error(true);
      if (e instanceof Error) {
        setSignupStep1ErrorMessage(e.message || 'An error occurred during signup. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const onAnswerSecurityQuestions = async (secQuestionsAnswer: ISecurityQuestionAnswer[]) => {
    setOpenSecurityQuestionsModal(false);
    submit({
      email: signupValidValues?.email || '',
      username: signupValidValues?.username || '',
      password: signupValidValues?.password || '',
      confirmPassword: signupValidValues?.confirmPassword || '',
      mfaPreference: (signupValidValues?.mfaPreference as 'email' | 'authenticator') || 'email',
      securityQuestionAnswer: secQuestionsAnswer,
    });
  };

  return (
    <>
      {loading && <FullScreenLoading />}

      <Container>
        <MenuContainer>
          <FormContainer>
            <Logo src={logoSvg} alt="Pride Global logo" />
            <FormTitle>{t('signUp.form.title')}</FormTitle>
            {!searchParams.get('token') && (
              <Step1Wrapper>
                <Input
                  placeholder={t('signUpWizard.step2.businessEmail')}
                  onChange={e => {
                    handleData({ businessEmail: e.target.value });
                  }}
                  type="email"
                  id="email"
                  name="email"
                  value={step1Data.businessEmail}
                  required
                />
                <Input
                  placeholder={t('signUpWizard.step3.ssn')}
                  onChange={e => {
                    handleData({ lastNumbersSsn: e.target.value });
                  }}
                  type="text"
                  id="ssn"
                  name="ssn"
                  value={step1Data.lastNumbersSsn}
                  required
                />
                <CustomDateWithPlaceholder
                  onChange={e => {
                    handleData({ birthDate: e.target.value });
                  }}
                  id="birthDate"
                  name="birthDate"
                  placeholder={t('signUpWizard.step3.birthDate')}
                  max="2008-12-31"
                  value={step1Data.birthDate}
                  required
                />
                {signupStep1Error && <RedText style={{ maxWidth: '400px' }}>{signupStep1ErrorMessage}</RedText>}
                {signupStep1Error && (
                  <RedText style={{ maxWidth: '400px' }}>{t('signUpWizard.errornewjoinee')}</RedText>
                )}
                <LargeButton type="button" onClick={handleVerification}>
                  {t('signUpWizard.next')}
                </LargeButton>
              </Step1Wrapper>
            )}
            {searchParams.get('token') && (
              <>
                <Formik
                  initialValues={{
                    email: '',
                    username: '',
                    password: '',
                    confirmPassword: '',
                    mfaPreference: '' as 'email' | 'authenticator' | '',
                  }}
                  onSubmit={handleSecurityQuestions}
                  validationSchema={validationSchema}
                >
                  {({ handleChange, isSubmitting, handleSubmit, values, setFieldValue, errors, touched }) => (
                    <Form onSubmit={handleSubmit}>
                      <EmailInput
                        placeholder={t('signUp.form.personalEmail')}
                        handleChange={handleChange}
                        id="email"
                        name="email"
                      />
                      <ErrorMessage data-cy="signup-email-error" name="email" component={ErrorText} />

                      <SignUpInput>
                        <InputIcon src={personSVG} style={{ fill: 'black' }} />
                        <UsernameInput
                          id="username"
                          name="username"
                          type="text"
                          placeholder={t('signUp.form.username')}
                          onChange={handleChange}
                        />
                      </SignUpInput>
                      <ErrorMessage data-cy="signup-username-error" name="username" component={ErrorText} />

                      <PassInput placeholder={t('signUp.form.password')} handleChange={handleChange} />
                      <PassInput
                        placeholder={t('signUp.form.confirmPassword')}
                        name="confirmPassword"
                        id="confirmPassword"
                        handleChange={handleChange}
                      />
                      <ErrorMessage
                        data-cy="signup-password-error"
                        name="password"
                        component={() => (
                          <>
                            <GrayText>{t('signUp.form.passwordRestrictions.title')}</GrayText>
                            <RedText>{t('signUp.form.passwordRestrictions.item01')}</RedText>
                            <RedText>{t('signUp.form.passwordRestrictions.item02')}</RedText>
                            <RedText>{t('signUp.form.passwordRestrictions.item03')}</RedText>
                            <RedText>{t('signUp.form.passwordRestrictions.item04')}</RedText>
                            <RedText>{t('signUp.form.passwordRestrictions.item05')}</RedText>
                          </>
                        )}
                      />
                      <ErrorMessage
                        data-cy="signup-confirm-password-error"
                        name="confirmPassword"
                        component={RedText}
                      />
                      <div style={{ marginTop: '12px' }}>
                        <p style={{ fontSize: '13px', color: '#374151', marginBottom: '8px', fontWeight: 600 }}>
                          Verification method
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {(['authenticator', 'email'] as const).map(method => (
                            <label
                              key={method}
                              htmlFor={`mfaPreference-${method}`}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                padding: '12px 14px',
                                borderRadius: '10px',
                                border: values.mfaPreference === method ? '2px solid #4948ac' : '1px solid #d9def0',
                                background:
                                  values.mfaPreference === method
                                    ? 'linear-gradient(180deg,#eef1ff 0%,#dce2ff 100%)'
                                    : '#fafbff',
                                cursor: 'pointer',
                              }}
                            >
                              <input
                                type="radio"
                                id={`mfaPreference-${method}`}
                                name="mfaPreference"
                                value={method}
                                checked={values.mfaPreference === method}
                                onChange={() => setFieldValue('mfaPreference', method)}
                                style={{ accentColor: '#4948ac', width: '16px', height: '16px', flexShrink: 0 }}
                              />
                              <div>
                                <div style={{ fontSize: '13px', fontWeight: 700, color: '#2f3b8f' }}>
                                  {method === 'authenticator' ? 'Authenticator App' : 'Email Code'}
                                </div>
                                <div style={{ fontSize: '11px', color: '#556080', marginTop: '2px' }}>
                                  {method === 'authenticator'
                                    ? 'Use an authenticator app to generate codes'
                                    : 'Receive a one-time code by email'}
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                        {errors.mfaPreference && touched.mfaPreference && (
                          <RedText style={{ marginTop: '4px', fontSize: '12px' }}>{errors.mfaPreference}</RedText>
                        )}
                      </div>

                      <LargeButton type="submit" disabled={isSubmitting}>
                        {t('signUp.form.button')}
                      </LargeButton>
                    </Form>
                  )}
                </Formik>
                <SecurityQuestionsModal
                  isOpen={openSecurityQuestionsModal}
                  questions={securityQuestionAnswer}
                  onAnswer={onAnswerSecurityQuestions}
                />
                {signupError && <RedText>{t('login.signUp.serverError')}</RedText>}
              </>
            )}
            <Text>{t('signUp.login.title')}</Text>
            <RouterLink to="/">{t('signUp.login.link')}</RouterLink>
          </FormContainer>
          <FooterContainer>
            <FooterText>{`PRIDE GLOBAL ${currentYear} ALL RIGHTS RESERVED`}</FooterText>
            <LinkContainer>
              <RouterLink style={{ fontSize: '12px' }} to="https://prideglobal.com/terms-of-use/" target="_blank">
                {t('signUp.footer.termsOfUse')}
              </RouterLink>
              <RouterLink style={{ fontSize: '12px' }} to="https://prideglobal.com/privacy-policy/" target="_blank">
                {t('signUp.footer.privacyPolicy')}
              </RouterLink>
              <RouterLink style={{ fontSize: '12px' }} to="mailto:employeesupport@prideglobal.com" target="_blank">
                {t('signUp.footer.support')}
              </RouterLink>
            </LinkContainer>
          </FooterContainer>
        </MenuContainer>
        <ImageContainer>
          <BackgroundImage src={background} />
        </ImageContainer>
      </Container>
    </>
  );
};

export default SignUp;
